import { Checkbox, Grid, Select, Slider, Text, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { formatInputValue, mgaRegex, responsiveCol } from "../../../../constants/helper";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { WEEK_DAYS } from "../../../../utils/tarifSharedUtils";
import DistributionChart, { createDistribution } from "./DistributionChart";
import ProjectNumberInput from "../../../common/ProjectNumberInput";
import { formatProjectNumberRange } from "../../../../utils/helpers";
import {
  TRIP_DISTRIBUTION_PROFILES,
  calculateTripDistribution,
} from "./tripDistributionProfiles";
import {
  ARRIVAL_DISTRIBUTION_PROFILES,
  calculateArrivalDistribution,
} from "./arrivalDistributionProfiles";

const DISCRETIZATION = 1;

const required = (label) => (value) =>
  value === null || value === undefined || String(value).trim() === ""
    ? `${label} is required.`
    : null;

const number = (label, min, max) => (value) => {
  const requiredError = required(label)(value);
  if (requiredError) return requiredError;
  const numberValue = Number(value);
  if (!Number.isFinite(numberValue)) return `${label} must be a valid number.`;
  if (min !== undefined && numberValue < min) return `${label} must be at least ${min}.`;
  if (max !== undefined && numberValue > max) return `${label} must be at most ${max}.`;
  return null;
};

const rangedNumber = (label, min, max, regex, errorMessage) => (value) => {
  const requiredError = required(label)(value);
  if (requiredError) return requiredError;
  const stringValue = String(value).trim();
  const normalizedValue = formatInputValue(stringValue);
  const numberValue = Number(normalizedValue);
  return regex.test(normalizedValue) && numberValue >= min && numberValue <= max
    ? null
    : errorMessage ||
      `${label} should be between ${min} and ${max} and max 2 decimals allowed.`;
};

const getFleetValue = (fleet, path) =>
  path.split(".").reduce((value, key) => value?.[key], fleet);

export const getProbabilityArea = (profile = []) => {
  const points = Array.isArray(profile) ? profile : [];
  const values = points.map((point) =>
    typeof point === "number"
      ? point
      : point?.y ?? point?.[1] ?? point?.probability,
  );
  const coordinates = points.map((point) =>
    typeof point === "number"
      ? null
      : point?.x ?? point?.[0] ?? point?.midpoint,
  );
  const isSteppedProfile =
    coordinates.some((coordinate) => coordinate !== null) &&
    coordinates.some((coordinate, index) => coordinate === coordinates[index - 1]);
  const binValues = isSteppedProfile
    ? values.filter((_, index) => index % 2 === 1)
    : values;

  return binValues.reduce(
    (total, probability) =>
      total + (Number.isFinite(Number(probability)) ? Number(probability) : 0),
    0,
  );
};

const hasFullProbabilityArea = (profile) =>
  Math.abs(getProbabilityArea(profile) - 100) <= 0.1;

export const getStoredOrGeneratedProfile = (
  distributionKey,
  distribution = {},
) => {
  const storedProfile =
    distributionKey === "arrivalDistribution"
      ? distribution.arrivalProfile
      : distribution.tripProfile;

  return Array.isArray(storedProfile) && storedProfile.length
    ? storedProfile
    : getGraphProfile(distributionKey, distribution);
};

const getGraphProfile = (distributionKey, distribution) => {
  const points = createDistribution({
    average:
      distributionKey === "arrivalDistribution"
        ? distribution.averageHourOfArrival
        : distribution.averageDistanceTravelled,
    standardDeviation: distribution.standardDeviation,
    minimum: 0,
    maximum:
      distribution.maximum ??
      (distributionKey === "arrivalDistribution" ? 24 : 1000),
    discretization: DISCRETIZATION,
    maxProbability: distribution.maxProbability,
    pointCount: distributionKey === "arrivalDistribution" ? 96 : 39,
  });

  return distributionKey === "arrivalDistribution"
    ? points
        .filter((_, index) => index % 2 === 1)
        .slice(0, -1)
        .map(([, probability]) => probability)
    : points
      .filter((_, index) => index % 2 === 1)
        .map(([x, y]) => ({ x, y }));
};

export const getChartDistributionData = (
  distributionKey,
  distribution = {},
  maximum,
) => {
  const resolvedMaximum = maximum ?? distribution.maximum ?? 1000;
  const profile = getChartProfile(
    distributionKey,
    distribution,
    resolvedMaximum,
  );
  if (distributionKey !== "tripDistribution" || !Array.isArray(profile)) {
    return profile;
  }

  const points = profile.map((point) =>
    Array.isArray(point)
      ? point
      : [
          Number(point?.x ?? point?.[0] ?? point?.midpoint),
          Number(point?.y ?? point?.[1] ?? point?.probability),
        ],
  );
  if (
    !points.length ||
    points.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))
  ) {
    return getChartDistributionData(distributionKey, {
      ...distribution,
      profileName: undefined,
      tripProfile: undefined,
      maximum: resolvedMaximum,
    }, resolvedMaximum);
  }

  const maximumPoint = Number(resolvedMaximum);
  const bins = points.flatMap(([start, probability], index) => {
    const nextStart = points[index + 1]?.[0];
    if (nextStart === undefined || nextStart <= start || start >= maximumPoint) {
      return [];
    }
    const end = Math.min(nextStart, maximumPoint);
    return [{
      start,
      end,
      // A clipped bin contributes only the fraction inside the driving range.
      weight: probability * (end - start) / (nextStart - start),
    }];
  });
  const total = bins.reduce((sum, bin) => sum + bin.weight, 0);
  const probabilities = bins.map((bin) =>
    total > 0 ? (bin.weight / total) * 100 : 0,
  );
  if (total > 0) {
    let lastPositiveIndex = probabilities.length - 1;
    while (lastPositiveIndex >= 0 && probabilities[lastPositiveIndex] <= 0) {
      lastPositiveIndex -= 1;
    }
    probabilities[lastPositiveIndex] +=
      100 - probabilities.reduce((sum, probability) => sum + probability, 0);
  }

  const chartPoints = [[Math.min(points[0][0], maximumPoint), 0]];
  bins.forEach(({ start, end }, index) => {
    chartPoints.push([start, probabilities[index]], [end, probabilities[index]]);
  });
  if (bins.length && bins[bins.length - 1].end < maximumPoint) {
    chartPoints.push([bins[bins.length - 1].end, 0], [maximumPoint, 0]);
  }
  chartPoints.push([maximumPoint, 0]);
  return chartPoints;
};

export const getNormalizedTripProfile = (distribution = {}, maximum) => {
  const chartPoints = getChartDistributionData(
    "tripDistribution",
    distribution,
    maximum,
  );
  const profile = chartPoints
    .filter((_, index) => index % 2 === 1)
    .map(([x, y]) => ({ x, y: Number(y) || 0 }));
  return profile;
};

const getChartProfile = (distributionKey, distribution = {}, maximum) => {
  const hasStoredProfile =
    (distributionKey === "arrivalDistribution" &&
      Array.isArray(distribution.arrivalProfile)) ||
    (distributionKey === "tripDistribution" &&
      Array.isArray(distribution.tripProfile));

  return hasStoredProfile
    ? getStoredOrGeneratedProfile(distributionKey, distribution)
    : getGraphProfile(distributionKey, { ...distribution, maximum });
};

// Builds the 0-24h step-chart data for a stored arrival profile. Handles the
// saved flat probability-array shape (fixed 15-min buckets, x is implicit)
// as well as the legacy {x, y}/[x, y] point shapes. Returns undefined for
// missing/invalid profiles so the chart falls back to its own generated bell
// curve, unchanged from prior behavior.
const getArrivalChartData = (arrivalProfile) => {
  if (!Array.isArray(arrivalProfile) || !arrivalProfile.length) {
    return undefined;
  }

  const isFlatProfile = arrivalProfile.every(
    (point) => typeof point === "number" || typeof point === "string",
  );
  if (isFlatProfile) {
    const HOURS_PER_BUCKET = 0.25; // fixed 15-min bucket size
    const chartPoints = [[0, 0]];
    arrivalProfile.forEach((value, index) => {
      const probability = Number(value) || 0;
      const start = Number((index * HOURS_PER_BUCKET).toFixed(2));
      const end = Number(((index + 1) * HOURS_PER_BUCKET).toFixed(2));
      chartPoints.push([start, probability], [end, probability]);
    });
    chartPoints.push([24, 0]);
    return chartPoints;
  }

  const points = arrivalProfile.map((point) =>
    Array.isArray(point) ? point : [Number(point?.x), Number(point?.y)],
  );
  if (points.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) {
    return undefined;
  }

  const chartPoints = [[Math.min(points[0][0], 24), 0]];
  points.forEach(([start, probability], index) => {
    if (start >= 24) return;
    const end = Math.min(points[index + 1]?.[0] ?? 24, 24);
    if (end > start) {
      chartPoints.push([start, probability], [end, probability]);
    }
  });
  chartPoints.push([24, 0]);
  return chartPoints;
};

export const getFleetValidationErrors = (
  fleet = {},
  t = (key) => key,
  numberFormat,
) => {
  const errors = {};
  const range = (field, min, max, regex, messageKey = "fleet.validation.range") =>
    rangedNumber(
      t(`fleet.validation.${field}`),
      min,
      max,
      regex,
      t(messageKey, {
        field: t(`fleet.validation.${field}`),
        ...formatProjectNumberRange(min, max, numberFormat),
      }),
    );
  const rules = {
    vehicleCategoryId: number(t("fleet.validation.vehicleCategory")),
    vehicleModelName: (value) =>
      value === null || value === undefined || String(value).trim() === ""
        ? t("fleet.validation.vehicleModelRequired")
        : null,
    fleetSize: range(
      "fleetSize",
      1,
      100,
      mgaRegex.oneToHundredWithoutDecimal,
      "evcs.validation.integerRange",
    ),
    batteryCapacity: range("batteryCapacity", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
    drivingRange: range("drivingRange", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
    energyConsumption: range("averageConsumption", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
    dcChargingPower: range("dcChargingPower", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
    chargingEfficiency: range("chargingEfficiency", 0, 100, mgaRegex.zeroToHundredWithDecimal),
    averageParkingTime: range("averageParkingTime", 1, 24, mgaRegex.oneTo24WithTwoDecimalRegex),
    parkingTimeStdDeviation: range("parkingTimeStdDeviation", 1, 24, mgaRegex.oneTo24WithTwoDecimalRegex),
    "arrivalDistribution.profileName": required(t("fleet.validation.arrivalProfile")),
    "arrivalDistribution.averageHourOfArrival": range(
      "averageArrivalHour",
      1,
      24,
      mgaRegex.oneTo24WithTwoDecimalRegex,
    ),
    "arrivalDistribution.standardDeviation": range(
      "arrivalStandardDeviation",
      1,
      24,
      mgaRegex.oneTo24WithTwoDecimalRegex,
    ),
    "arrivalDistribution.maxProbability": range("arrivalMaxProbability", 0.01, 100, mgaRegex.zeroPointOneToHundredWithTwoDecimalRegex),
    "tripDistribution.profileName": required(t("fleet.validation.tripProfile")),
    "tripDistribution.averageDistanceTravelled": range(
      "averageDistance",
      1,
      9999,
      mgaRegex.oneTo9999WithTwoDecimalRegex,
    ),
    "tripDistribution.standardDeviation": range(
      "tripStandardDeviation",
      1,
      999,
      mgaRegex.oneTo999WithTwoDecimalRegex,
    ),
    "tripDistribution.maxProbability": range("tripMaxProbability", 0.01, 100, mgaRegex.zeroPointOneToHundredWithTwoDecimalRegex),
  };

  // Numeric distribution fields are only editable/required when "custom" is selected.
  if (getFleetValue(fleet, "arrivalDistribution.profileName") !== "custom") {
    delete rules["arrivalDistribution.averageHourOfArrival"];
    delete rules["arrivalDistribution.standardDeviation"];
    delete rules["arrivalDistribution.maxProbability"];
  }
  if (getFleetValue(fleet, "tripDistribution.profileName") !== "custom") {
    delete rules["tripDistribution.averageDistanceTravelled"];
    delete rules["tripDistribution.standardDeviation"];
    delete rules["tripDistribution.maxProbability"];
  }

  Object.entries(rules).forEach(([path, validate]) => {
    const value = getFleetValue(fleet, path);
    const error = validate(value);
    if (error) errors[path] = error;
  });

  const tripDays = fleet.arrivalDistribution?.tripDays;
  if (!Array.isArray(tripDays) || !tripDays.length) {
    errors["arrivalDistribution.tripDays"] =
      t("fleet.validation.tripDayRequired");
  } else if (tripDays.some((day) => !WEEK_DAYS.some(({ id }) => Number(day) === id))) {
    errors["arrivalDistribution.tripDays"] =
      t("fleet.validation.tripDayRange");
  }

  const arrivalProfile = getChartProfile(
    "arrivalDistribution",
    fleet.arrivalDistribution,
    24,
  );
  if (!hasFullProbabilityArea(arrivalProfile)) {
    errors["arrivalDistribution.probabilityArea"] =
      t("fleet.validation.arrivalProbabilityArea");
  }
  const tripProfile = getChartDistributionData(
    "tripDistribution",
    fleet.tripDistribution,
    fleet.drivingRange,
  );
  if (!hasFullProbabilityArea(tripProfile)) {
    errors["tripDistribution.probabilityArea"] =
      t("fleet.validation.tripProbabilityArea");
  }

  return errors;
};

export const validateFleet = (fleet = {}, t) =>
  Object.values(getFleetValidationErrors(fleet, t))[0] || null;

const Fleet = ({
  fleet = {},
  distributions = [],
  assetSubtypeList = [],
  evcsAssetList = [],
  onFleetChange,
  onAddCustomAsset,
  validationErrors = {},
}) => {
  const { t } = useTranslation();
  const fleetRef = useRef(null);
  const defaultCategoryAppliedRef = useRef(null);
  const range = (field, min, max, regex, messageKey = "fleet.validation.range") =>
    rangedNumber(
      t(`fleet.validation.${field}`),
      min,
      max,
      regex,
      t(messageKey, {
        field: t(`fleet.validation.${field}`),
        min,
        max,
      }),
    );
  const form = useForm({
    key: JSON.stringify(fleet),
    initialValues: fleet,
    validate: {
      vehicleCategoryId: number(t("fleet.validation.vehicleCategory")),
      vehicleModelName: (value) =>
        value === null || value === undefined || String(value).trim() === ""
          ? t("fleet.validation.vehicleModelRequired")
          : null,
      fleetSize: range(
        "fleetSize",
        1,
        100,
        mgaRegex.oneToHundredWithoutDecimal,
        "evcs.validation.integerRange",
      ),
      batteryCapacity: range("batteryCapacity", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
      drivingRange: range("drivingRange", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
      energyConsumption: range("averageConsumption", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
      dcChargingPower: range("dcChargingPower", 1, 9999, mgaRegex.oneTo9999WithTwoDecimalRegex),
      chargingEfficiency: range("chargerEfficiency", 0, 100, mgaRegex.zeroToHundredWithDecimal),
      averageParkingTime: range("averageParkingTime", 1, 24, mgaRegex.oneTo24WithTwoDecimalRegex),
      parkingTimeStdDeviation: range("parkingTimeStdDeviation", 1, 24, mgaRegex.oneTo24WithTwoDecimalRegex),
      arrivalDistribution: {
        profileName: required(t("fleet.validation.arrivalProfile")),
        averageHourOfArrival: (value, values) =>
          values?.arrivalDistribution?.profileName !== "custom"
            ? null
            : range(
                "averageArrivalHour",
                1,
                24,
                mgaRegex.oneTo24WithTwoDecimalRegex,
              )(value),
        standardDeviation: (value, values) =>
          values?.arrivalDistribution?.profileName !== "custom"
            ? null
            : range(
                "arrivalStandardDeviation",
                1,
                24,
                mgaRegex.oneTo24WithTwoDecimalRegex,
              )(value),
        maxProbability: (value, values) =>
          values?.arrivalDistribution?.profileName !== "custom"
            ? null
            : range("arrivalMaxProbability", 0.01, 100, mgaRegex.zeroPointOneToHundredWithTwoDecimalRegex)(value),
        tripDays: (value) =>
          !Array.isArray(value) || !value.length
            ? t("fleet.validation.tripDayRequired")
            : value.some((day) => !WEEK_DAYS.some(({ id }) => Number(day) === id))
              ? t("fleet.validation.tripDayRange")
              : null,
      },
      tripDistribution: {
        profileName: required(t("fleet.validation.tripProfile")),
        averageDistanceTravelled: (value, values) =>
          values?.tripDistribution?.profileName !== "custom"
            ? null
            : range(
                "averageDistance",
                1,
                9999,
                mgaRegex.oneTo9999WithTwoDecimalRegex,
              )(value),
        standardDeviation: (value, values) =>
          values?.tripDistribution?.profileName !== "custom"
            ? null
            : range(
                "tripStandardDeviation",
                1,
                999,
                mgaRegex.oneTo999WithTwoDecimalRegex,
              )(value),
        maxProbability: (value, values) =>
          values?.tripDistribution?.profileName !== "custom"
            ? null
            : range("tripMaxProbability", 0.01, 100, mgaRegex.zeroPointOneToHundredWithTwoDecimalRegex)(value),
      },
    },
  });
  const { setFieldValue, setValues, values } = form;
  const getError = (path) => validationErrors[path] || form.errors[path];

  useEffect(() => {
    if (!Object.keys(validationErrors).length) return;

    const errorPaths = Object.keys(validationErrors);
    const fields = Array.from(
      fleetRef.current?.querySelectorAll("[name]") || [],
    );
    const invalidField =
      errorPaths
        .map((path) => fields.find((field) => field.name === path))
        .find(Boolean) ||
      fleetRef.current?.querySelector('[aria-invalid="true"]');
    invalidField?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [validationErrors]);

  useEffect(() => {
    setValues(fleet);
  }, [fleet, setValues]);

  const arrivalDistribution = Array.isArray(values.arrivalDistribution)
    ? {}
    : values.arrivalDistribution || {};
  const tripDistribution = Array.isArray(values.tripDistribution)
    ? {}
    : values.tripDistribution || {};
  // Arrival Distribution dropdown is UI-driven (static profile catalogue), not API-sourced.
  // The option value is the profile name itself, matching the profileName strings already stored in the DB.
  const arrivalDistributionOptions = [
    { value: "custom", label: t("fleet.custom") },
    ...ARRIVAL_DISTRIBUTION_PROFILES.map(({ name }) => ({
      value: name,
      label: name,
    })),
  ];
  // Trip Distribution dropdown is UI-driven (static profile catalogue), not API-sourced.
  // The option value is the profile name itself, matching the profileName strings already stored in the DB.
  const tripDistributionOptions = [
    { value: "custom", label: t("fleet.custom") },
    ...TRIP_DISTRIBUTION_PROFILES.map(({ name }) => ({
      value: name,
      label: name,
    })),
  ];
  const assetSubtypeOptions = assetSubtypeList.map(({ id, type }) => ({
    value: String(id),
    label: type,
  }));
  const defaultVehicleCategoryId = assetSubtypeOptions[0]?.value;
  const vehicleCategoryId =
    fleet.vehicleCategoryId === null || fleet.vehicleCategoryId === undefined ||
    fleet.vehicleCategoryId === ""
      ? defaultVehicleCategoryId
      : String(fleet.vehicleCategoryId);
  const vehicleModelOptions = evcsAssetList
    .filter(
      ({ assetSubType }) =>
        String(assetSubType?.id) === String(vehicleCategoryId),
    )
    .map(({ communicationModel }) =>
      communicationModel
        ? { value: communicationModel, label: communicationModel }
        : null,
    )
    .filter(Boolean);
  vehicleModelOptions.unshift({ value: "0", label: t("fleet.custom") });
  const vehicleModelValue =
    fleet?.vehicleModelName === null || fleet?.vehicleModelName === undefined
      ? null
      : String(fleet.vehicleModelName);
  const fleetIdentifier = fleet?.fleetKey ?? fleet?.fleetId;
  useEffect(() => {
    if (
      fleet.vehicleCategoryId !== null &&
      fleet.vehicleCategoryId !== undefined &&
      fleet.vehicleCategoryId !== ""
    ) {
      defaultCategoryAppliedRef.current = null;
      return;
    }
    if (defaultVehicleCategoryId === undefined) return;

    const defaultCategoryKey = `${fleetIdentifier ?? ""}:${defaultVehicleCategoryId}`;
    if (defaultCategoryAppliedRef.current === defaultCategoryKey) return;
    defaultCategoryAppliedRef.current = defaultCategoryKey;
    onFleetChange?.(fleetIdentifier, "vehicleCategoryId", defaultVehicleCategoryId);
  }, [
    defaultVehicleCategoryId,
    fleet.vehicleCategoryId,
    fleetIdentifier,
    onFleetChange,
    setFieldValue,
  ]);
  const handleCategoryChange = (value) => {
    setFieldValue("vehicleCategoryId", value);
    onFleetChange?.(fleetIdentifier, "vehicleCategoryId", value);
  };
  const handleInputChange = (field) => (event) => {
    const value = event.currentTarget.value;
    if (!mgaRegex.numericInputRegex.test(value)) return;
    setFieldValue(field, value);
    onFleetChange?.(fleetIdentifier, field, value);
  };
  const handleSliderChange = (field) => (value) => {
    const nextValue = String(value);
    setFieldValue(field, nextValue);
    onFleetChange?.(fleetIdentifier, field, nextValue);
  };
  const handleFleetSizeChange = (event) => {
    const value = event.currentTarget.value;
    if (!mgaRegex.integerInputRegex.test(value)) return;
    setFieldValue("fleetSize", value);
    onFleetChange?.(fleetIdentifier, "fleetSize", value);
  };
  const handleDistributionChange = (distributionKey, field) => (value) => {
    const distribution =
      distributionKey === "arrivalDistribution"
        ? arrivalDistribution
        : tripDistribution;
    const nextValue = value?.currentTarget?.value ?? value ?? "";
    if (!mgaRegex.numericInputRegex.test(nextValue)) return;
    if (String(distribution[field] ?? "") === String(nextValue)) return;
    const profileField =
      distributionKey === "arrivalDistribution"
        ? "arrivalProfile"
        : "tripProfile";
    const nextDistribution = {
      ...distribution,
      [field]: nextValue,
      profileName: "custom",
    };
    delete nextDistribution[profileField];
    setFieldValue(`${distributionKey}.${field}`, nextValue);
    setFieldValue(`${distributionKey}.profileName`, "custom");
    onFleetChange?.(fleetIdentifier, {
      [distributionKey]: nextDistribution,
    });
  };
  // Arrival Distribution: picking a named profile recalculates the step-chart
  // curve via calculateArrivalDistribution and zeroes averageHourOfArrival/
  // standardDeviation/maxProbability (not required/editable outside "custom").
  // Editing any of those fields (see handleDistributionChange) flips the dropdown to "custom".
  const handleArrivalDistributionProfileChange = (value) => {
    if (value === "custom") {
      const customDistribution = {
        ...arrivalDistribution,
        profileName: "custom",
      };
      onFleetChange?.(fleetIdentifier, {
        arrivalDistribution: customDistribution,
      });
      setValues((currentValues) => ({
        ...currentValues,
        arrivalDistribution: customDistribution,
      }));
      return;
    }

    const selectedProfile = ARRIVAL_DISTRIBUTION_PROFILES.find(
      ({ name }) => name === value,
    );
    if (!selectedProfile) return;

    const { x, probability } = calculateArrivalDistribution(selectedProfile);
    const arrivalProfile = x.map((xValue, index) => ({
      x: xValue,
      y: probability[index],
    }));
    const nextArrivalDistribution = {
      ...arrivalDistribution,
      profileName: selectedProfile.name,
      arrivalProfile,
      averageHourOfArrival: 0,
      standardDeviation: 0,
      maxProbability: 0,
    };

    onFleetChange?.(fleetIdentifier, {
      arrivalDistribution: nextArrivalDistribution,
    });
    setValues((currentValues) => ({
      ...currentValues,
      arrivalDistribution: nextArrivalDistribution,
    }));
  };

  // Trip Distribution: picking a named profile recalculates the step-chart
  // curve via calculateTripDistribution and zeroes averageDistanceTravelled/
  // standardDeviation/maxProbability (not required/editable outside "custom").
  // Editing any of those fields (see handleDistributionChange) flips the dropdown to "custom".
  const handleTripDistributionProfileChange = (value) => {
    if (value === "custom") {
      const customDistribution = {
        ...tripDistribution,
        profileName: "custom",
      };
      onFleetChange?.(fleetIdentifier, {
        tripDistribution: customDistribution,
      });
      setValues((currentValues) => ({
        ...currentValues,
        tripDistribution: customDistribution,
      }));
      return;
    }

    const selectedProfile = TRIP_DISTRIBUTION_PROFILES.find(
      ({ name }) => name === value,
    );
    if (!selectedProfile) return;

    const { x, probability } = calculateTripDistribution(selectedProfile);
    const tripProfile = x.map((xValue, index) => ({
      x: xValue,
      y: probability[index],
    }));
    const nextTripDistribution = {
      ...tripDistribution,
      profileName: selectedProfile.name,
      tripProfile,
      averageDistanceTravelled: 0,
      standardDeviation: 0,
      maxProbability: 0,
    };

    onFleetChange?.(fleetIdentifier, {
      tripDistribution: nextTripDistribution,
    });
    setValues((currentValues) => ({
      ...currentValues,
      tripDistribution: nextTripDistribution,
    }));
  };
  const handleChartDistributionChange = (distributionKey) => (points) => {
    const distribution =
      distributionKey === "arrivalDistribution"
        ? arrivalDistribution
        : tripDistribution;
    const bins = [];
    for (let index = 1; index < points.length - 1; index += 2) {
      const start = Number(points[index][0]);
      const end = Number(points[index + 1][0]);
      const probability = Math.max(0, Number(points[index][1]) || 0);
      bins.push({ midpoint: (start + end) / 2, probability });
    }
    const total = bins.reduce((sum, bin) => sum + bin.probability, 0);

    // Derive average/standard deviation/max probability from the plotted curve
    // itself (instead of leaving them at a stale 0 from a prior named-profile
    // selection), clamped to each field's valid range so "custom" validation
    // (which stays mandatory) never fails after a direct chart edit.
    const clampToRange = (value, min, max) => Math.min(max, Math.max(min, value));
    let statsFields = {};
    if (total > 0) {
      const mean = bins.reduce((sum, bin) => sum + bin.midpoint * bin.probability, 0) / total;
      const variance = bins.reduce((sum, bin) => sum + bin.probability * (bin.midpoint - mean) ** 2, 0) / total;
      const stdDeviation = Math.sqrt(variance);
      const maxBinProbability = bins.reduce((max, bin) => Math.max(max, bin.probability), 0);
      statsFields =
        distributionKey === "arrivalDistribution"
          ? {
              averageHourOfArrival: Number(clampToRange(mean, 1, 24).toFixed(2)),
              standardDeviation: Number(clampToRange(stdDeviation, 1, 24).toFixed(2)),
              maxProbability: Number(clampToRange(maxBinProbability, 0.01, 100).toFixed(2)),
            }
          : {
              averageDistanceTravelled: Number(clampToRange(mean, 1, 9999).toFixed(2)),
              standardDeviation: Number(clampToRange(stdDeviation, 1, 999).toFixed(2)),
              maxProbability: Number(clampToRange(maxBinProbability, 0.01, 100).toFixed(2)),
            };
    }

    const fields =
      distributionKey === "arrivalDistribution"
        ? {
            ...statsFields,
            arrivalProfile: points
              .filter((_, index) => index % 2 === 1)
              .slice(0, -1)
              .map(([x, y]) => ({ x, y })),
          }
        : {
            ...statsFields,
            tripProfile: points
              .filter((_, index) => index % 2 === 1)
              .map(([x, y]) => ({ x, y })),
          };

    onFleetChange?.(fleetIdentifier, {
      [distributionKey]: { ...distribution, profileName: "custom", ...fields },
    });
    setValues((currentValues) => ({
      ...currentValues,
      [distributionKey]: {
        ...currentValues?.[distributionKey],
        ...distribution,
        profileName: "custom",
        ...fields,
      },
    }));
  };
  const handleModelChange = (value) => {
    setFieldValue("vehicleModelName", value);
    const selectedAsset = evcsAssetList.find(
      ({ communicationModel }) => communicationModel === value,
    );
    const attributes = Object.fromEntries(
      (selectedAsset?.assetAttributes || []).map(
        ({ type, value: attributeValue }) => [type, attributeValue ?? ""],
      ),
    );

    onFleetChange?.(fleetIdentifier, {
      vehicleModelName: value,
      assetId: selectedAsset?.assetId,
      batteryCapacity: attributes.BatteryCapacity || "",
      drivingRange: attributes.DrivingRange || "",
      energyConsumption: attributes.AvgConsumption || "",
      dcChargingPower: attributes.DcFastCharging || "",
      chargingEfficiency: attributes.ChargerEfficiency || "",
    });
  };
  const tripDays = Array.isArray(arrivalDistribution?.tripDays)
    ? arrivalDistribution?.tripDays
        .map(Number)
        .filter((day) => WEEK_DAYS?.some(({ id }) => id === day))
    : [];
  const handleTripDayChange = (day, checked) => {
    const nextTripDays = checked
      ? [...new Set([...tripDays, day])].sort(
          (firstDay, secondDay) => firstDay - secondDay,
        )
      : tripDays.filter((selectedDay) => selectedDay !== day);
    onFleetChange?.(fleetIdentifier, {
      arrivalDistribution: { ...arrivalDistribution, tripDays: nextTripDays },
    });
  };

  return (
    <div ref={fleetRef}>
      <Text component="h5" size="sm" fw={600}>
        {t("fleet.vehicleDetails")}
      </Text>
      <Grid gutter="md" className="mb-4">
        <Grid.Col {...responsiveCol}>
          <Select
            rightSection={<IconCaretDownFilled size={18} />}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            label={t("fleet.selectVehicleCategory")}
            data={assetSubtypeOptions}
          {...form.getInputProps("vehicleCategoryId")}
            error={getError("vehicleCategoryId")}
            value={vehicleCategoryId ?? null}
            onChange={handleCategoryChange}
            withinPortal
            required
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <div style={{ display: "flex", gap: "8px", alignItems: !getError("vehicleModelName") ? "flex-end" : "center" }}>
            <div style={{ flex: 1 }}>
              <Select
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("fleet.selectVehicleModel")}
                data={vehicleModelOptions}
                {...form.getInputProps("vehicleModelName")}
                error={getError("vehicleModelName")}
                value={vehicleModelValue}
                onChange={(value) => {
                  if (value === "0") {
                    setFieldValue("vehicleModelName", value);
                    onFleetChange?.(fleetIdentifier, { vehicleModelName: "0", assetId: null });
                    return;
                  }
                  handleModelChange(value);
                }}
                withinPortal
                required
                onClick={(event) => event.stopPropagation()}
              />
            </div>
            {vehicleModelValue === "0" && (
              <se-button
                aria-label="evcs-add-custom"
                onClick={() =>
                  onAddCustomAsset?.(fleetIdentifier, fleet?.vehicleCategoryId)
                }
                style={{ alignItems: "center", whiteSpace: "nowrap" }}
              >
                {t("fleet.addCustom")}
              </se-button>
            )}
          </div>
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.fleetSize")}
            {...form.getInputProps("fleetSize")}
            error={getError("fleetSize")}
            value={fleet.fleetSize ?? ""}
            onChange={handleFleetSizeChange}
            required
          />
        </Grid.Col>
      </Grid>
      <Text component="h5" size="sm" fw={600} className="mt-2">
        {t("fleet.technicalSpecifications")}
      </Text>
      <Grid gutter="md" className="mb-4">
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.batteryCapacity")}
            {...form.getInputProps("batteryCapacity")}
            error={getError("batteryCapacity")}
            value={fleet.batteryCapacity ?? ""}
            onChange={handleInputChange("batteryCapacity")}
            required
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "0.5rem",
                }}
              >
                {t("fleet.units.kWh")}
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.drivingRange")}
            rightSection={t("fleet.units.km")}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            {...form.getInputProps("drivingRange")}
            error={getError("drivingRange")}
            value={fleet.drivingRange ?? ""}
            onChange={handleInputChange("drivingRange")}
            required
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.averageConsumption")}
            rightSection={
              <div
                style={{
                  padding: "4px",
                  marginRight: "1.5rem",
                }}
              >
                {t("fleet.units.WhPerKm")}
              </div>
            }
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            {...form.getInputProps("energyConsumption")}
            error={getError("energyConsumption")}
            value={fleet.energyConsumption ?? ""}
            onChange={handleInputChange("energyConsumption")}
            required
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.dcFastCharging")}
            rightSection={t("fleet.units.kW")}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            {...form.getInputProps("dcChargingPower")}
            error={getError("dcChargingPower")}
            value={fleet.dcChargingPower ?? ""}
            onChange={handleInputChange("dcChargingPower")}
            required
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.chargerEfficiency")}
            rightSection={t("fleet.units.percent")}
            rightSectionProps={{ style: { pointerEvents: "none" } }}
            {...form.getInputProps("chargingEfficiency")}
            error={getError("chargingEfficiency")}
            value={fleet.chargingEfficiency ?? ""}
            onChange={handleInputChange("chargingEfficiency")}
            required
          />
        </Grid.Col>
      </Grid>
       <Text component="h5" size="sm" fw={600} className="mt-2">
        {t("fleet.parkingDistribution")}
      </Text>
      <Grid gutter="md" className="mb-5">
         <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.averageParkingTime")}
            {...form.getInputProps("averageParkingTime")}
            error={getError("averageParkingTime")}
            value={fleet.averageParkingTime ?? ""}
            onChange={handleInputChange("averageParkingTime")}
            required
          />
          <Slider
            min={0}
            max={24}
            step={1}
            precision={0}
            color="green"
            sx={{ marginTop: 24, marginBottom: 20 }}
            value={Number(fleet.averageParkingTime) || 0}
            onChange={handleSliderChange("averageParkingTime")}
            marks={[
              { value: 0, label: "0h" },
              { value: 6, label: "6h" },
              { value: 12, label: "12h" },
              { value: 18, label: "18h" },
              { value: 24, label: "24h" },
            ]}
          />
        </Grid.Col>
        <Grid.Col {...responsiveCol}>
          <ProjectNumberInput
            label={t("fleet.parkingStandardDeviation")}
            {...form.getInputProps("parkingTimeStdDeviation")}
            error={getError("parkingTimeStdDeviation")}
            value={fleet.parkingTimeStdDeviation ?? ""}
            onChange={handleInputChange("parkingTimeStdDeviation")}
            required
          />
          <Slider
            min={0}
            max={24}
            step={1}
            precision={0}
            color="green"
            sx={{ marginTop: 24, marginBottom: 20 }}
            value={Number(fleet.parkingTimeStdDeviation) || 0}
            onChange={handleSliderChange("parkingTimeStdDeviation")}
            marks={[
              { value: 0, label: "0h" },
              { value: 6, label: "6h" },
              { value: 12, label: "12h" },
              { value: 18, label: "18h" },
              { value: 24, label: "24h" },
            ]}
          />
        </Grid.Col>
        </Grid>
      <Grid gutter="md">
        <Grid.Col
          span={6}
          style={{
            borderRight: "1px solid #656668",
            padding: "0 24px 0 8px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Text component="h5" size="sm" fw={600}>
            {t("fleet.arrivalDistribution")}
          </Text>
          <Grid gutter="md" className="mb-4">
            <Grid.Col
              span={12}
              id="form-weekdays"
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-around",
                marginTop: "8px",
                marginBottom: "16px",
              }}
            >
              {WEEK_DAYS?.map(({ id, name }) => (
                <Checkbox
                  key={id}
                  label={<span className="font-10px line-spacing">{name}</span>}
                  checked={tripDays?.includes(id)}
                  size="lg"
                  color="green"
                  className="display-weekdays pointer"
                  styles={{
                    body: {
                      flexDirection: "column-reverse",
                      alignItems: "center",
                    },
                    label: { paddingLeft: "1px", marginBottom: "6px" },
                  }}
                  onChange={(event) =>
                    handleTripDayChange(id, event.currentTarget.checked)
                  }
                />
              ))}
            </Grid.Col>
            <Grid.Col span={12} style={{ minHeight: "20px" }}>
              {getError("arrivalDistribution.tripDays") && (
                <Text c="red" size="xs">
                  {getError("arrivalDistribution.tripDays")}
                </Text>
              )}
            </Grid.Col>
            <Grid.Col span={3}>
              <Select
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("fleet.profileName")}
                data={arrivalDistributionOptions}
                {...form.getInputProps("arrivalDistribution.profileName")}
                error={getError("arrivalDistribution.profileName")}
                value={arrivalDistribution.profileName || null}
                onChange={handleArrivalDistributionProfileChange}
                styles={{
                  item: {
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                    wordBreak: "keep-all",
                    overflowWrap: "normal",
                  },
                }}
                withinPortal
                required
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <ProjectNumberInput
                label={t("fleet.averageArrivalHour")}
                {...form.getInputProps("arrivalDistribution.averageHourOfArrival")}
                error={getError("arrivalDistribution.averageHourOfArrival")}
                value={arrivalDistribution.averageHourOfArrival ?? ""}
                onChange={handleDistributionChange(
                  "arrivalDistribution",
                  "averageHourOfArrival",
                )}
                required={arrivalDistribution.profileName === "custom"}
              />
            </Grid.Col>
            <Grid.Col span={3}>
              <ProjectNumberInput
                label={t("fleet.standardDeviation")}
                {...form.getInputProps("arrivalDistribution.standardDeviation")}
                error={getError("arrivalDistribution.standardDeviation")}
                value={arrivalDistribution.standardDeviation ?? ""}
                onChange={handleDistributionChange(
                  "arrivalDistribution",
                  "standardDeviation",
                )}
                required={arrivalDistribution.profileName === "custom"}
              />
            </Grid.Col>
            {/* <Grid.Col span={6}>
              <TextInput
                label={t("fleet.discretization")}
                {...form.getInputProps("arrivalDistribution.discretization")}
                error={getError("arrivalDistribution.discretization")}
                value={DISCRETIZATION}
                disabled
                required
              />
            </Grid.Col> */}
            <Grid.Col span={3}>
              <ProjectNumberInput
                label={t("fleet.maxProbability")}
                {...form.getInputProps("arrivalDistribution.maxProbability")}
                error={getError("arrivalDistribution.maxProbability")}
                value={arrivalDistribution.maxProbability ?? ""}
                onChange={handleDistributionChange(
                  "arrivalDistribution",
                  "maxProbability",
                )}
                required={arrivalDistribution.profileName === "custom"}
              />
            </Grid.Col>
          </Grid>
          {/* Grows to absorb any leftover height so this chart's top lines up with the Trip chart's top. */}
          {/* <div style={{ flexGrow: 1 }} /> */}
          <DistributionChart
            testId="fleet-arrival-distribution-chart"
            title={t("fleet.arrivalTimeDistribution")}
            xAxisTitle={t("fleet.arrivalTime")}
            average={arrivalDistribution.averageHourOfArrival}
            standardDeviation={arrivalDistribution.standardDeviation}
            minimum={0}
            maximum={24}
            tickInterval={1}
            discretization={DISCRETIZATION}
            maxProbability={arrivalDistribution.maxProbability}
            pointCount={96}
            profileName={arrivalDistribution.profileName}
            distributionData={getArrivalChartData(
              arrivalDistribution.arrivalProfile,
            )}
            onDistributionChange={handleChartDistributionChange(
              "arrivalDistribution",
            )}
            formatXAxis={(value) => value}
          />
        </Grid.Col>
        <Grid.Col
          span={6}
          style={{
            padding: "0 8px 0 24px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Text component="h5" size="sm" fw={600}>
            {t("fleet.tripDistribution")}
          </Text>
          <Grid gutter="md" className="mb-4">
            <Grid.Col span={6} style={{ marginTop: "8px" }}>
              <Select
                rightSection={<IconCaretDownFilled size={18} />}
                rightSectionProps={{ style: { pointerEvents: "none" } }}
                label={t("fleet.profileName")}
                data={tripDistributionOptions}
                {...form.getInputProps("tripDistribution.profileName")}
                error={getError("tripDistribution.profileName")}
                value={tripDistribution.profileName || null}
                onChange={handleTripDistributionProfileChange}
                styles={{
                  item: {
                    whiteSpace: "normal",
                    lineHeight: 1.3,
                    wordBreak: "keep-all",
                    overflowWrap: "normal",
                  },
                }}
                withinPortal
                required
              />
            </Grid.Col>
            <Grid.Col span={6} style={{ marginTop: "8px" }}>
              <ProjectNumberInput
                label={t("fleet.averageDistance")}
                {...form.getInputProps("tripDistribution.averageDistanceTravelled")}
                error={getError("tripDistribution.averageDistanceTravelled")}
                value={tripDistribution.averageDistanceTravelled ?? ""}
                onChange={handleDistributionChange(
                  "tripDistribution",
                  "averageDistanceTravelled",
                )}
                required={tripDistribution.profileName === "custom"}
              />
            </Grid.Col>
            {/* Mirrors Arrival's error-message row so "Standard Deviation"/"Max Probability" line up with Arrival's fields row. */}
            <Grid.Col span={12} style={{ minHeight: "3rem" }} aria-hidden="true" />
            <Grid.Col span={6}>
              <ProjectNumberInput
                label={t("fleet.standardDeviation")}
                {...form.getInputProps("tripDistribution.standardDeviation")}
                error={getError("tripDistribution.standardDeviation")}
                value={tripDistribution.standardDeviation ?? ""}
                onChange={handleDistributionChange(
                  "tripDistribution",
                  "standardDeviation",
                )}
                required={tripDistribution.profileName === "custom"}
              />
            </Grid.Col>
            {/* <Grid.Col span={6}>
              <TextInput
                label={t("fleet.discretization")}
                {...form.getInputProps("tripDistribution.discretization")}
                error={getError("tripDistribution.discretization")}
                value={DISCRETIZATION}
                disabled
                required
              />
            </Grid.Col> */}
            <Grid.Col span={6}>
              <ProjectNumberInput
                label={t("fleet.maxProbability")}
                {...form.getInputProps("tripDistribution.maxProbability")}
                error={getError("tripDistribution.maxProbability")}
                value={tripDistribution.maxProbability ?? ""}
                onChange={handleDistributionChange(
                  "tripDistribution",
                  "maxProbability",
                )}
                required={tripDistribution.profileName === "custom"}
              />
            </Grid.Col>
          </Grid>
          {/* Grows to absorb any leftover height so this chart's top lines up with the Arrival chart's top. */}
          {/* <div style={{ flexGrow: 1 }} /> */}
          <DistributionChart
            testId="fleet-trip-distance-distribution-chart"
            title={t("fleet.tripDistanceDistribution")}
            xAxisTitle={t("fleet.distanceTravelled")}
            average={tripDistribution.averageDistanceTravelled}
            standardDeviation={tripDistribution.standardDeviation}
            minimum={0}
            maximum={fleet.drivingRange}
            tickInterval={100}
            discretization={DISCRETIZATION}
            maxProbability={tripDistribution.maxProbability}
            pointCount={39}
            profileName={tripDistribution.profileName}
            referenceLineValue={fleet.drivingRange}
            xAxisDisplayMax={Math.max(
              Number(fleet.drivingRange) * 1.15,
              Number(tripDistribution.averageDistanceTravelled) * 1.15,
              100,
            )}
            distributionData={getChartDistributionData(
              "tripDistribution",
              tripDistribution,
              fleet.drivingRange,
            )}
            onDistributionChange={handleChartDistributionChange(
              "tripDistribution",
            )}
          />
        </Grid.Col>
      </Grid>
    </div>
  );
};
export default Fleet;